import { Box, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import {
  ActionIconButton,
  DateTimeText,
  GeneralTableBox,
  SortableHeadTableCell,
  NoDataSkeleton,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common';
import SupplyOrderStatusChip from './SupplyOrderStatusChip';
import SupplyOrderProjectChips from './SupplyOrderProjectChips';
import MaterialArticleChips from '../../../common/components/materials/MaterialArticleChips';
import {
  SUPPLY_ORDER_LIST_TEXTS as TEXTS,
  SUPPLY_ORDER_STATUSES,
  SUPPLY_ORDER_TABLE_TEXTS,
  SUPPLY_ORDER_ARTICLE_CHIP_CONFIG,
  SOURCE_TYPES,
} from '../constants/supplyOrdersConstants';

const textSx = { textAlign: 'left' };

const MIN_TABLE_WIDTH = 1400;
const NUMBER_CELL_SX = { fontWeight: 700, fontSize: 13, px: 1.5 };

const COLUMNS = [
  { sortKey: 'number', label: TEXTS.number, sx: { ...textSx, width: 150 } },
  { sortKey: 'origin_label', label: TEXTS.source, sx: { ...textSx, width: 240 } },
  { sortKey: 'destination_label', label: TEXTS.destination, sx: { ...textSx, width: 180 } },
  { label: TEXTS.project, sx: { ...textSx, width: 200 } },
  { label: SUPPLY_ORDER_TABLE_TEXTS.articles, sx: { ...textSx, width: 280 } },
  { sortKey: 'order_date', label: TEXTS.date, sx: { ...textSx, width: 110 } },
  {
    sortKey: 'provider_delivery_commitment_at',
    label: TEXTS.commitment,
    sx: { ...textSx, width: 120 },
  },
  { sortKey: 'status_label', label: TEXTS.status, sx: { width: 170 } },
  { sortKey: 'external_order_sort', label: TEXTS.externalOrder, sx: { ...textSx, width: 140 } },
  { label: TEXTS.actions, sx: { width: 100 } },
];

/**
 * Tabla de órdenes de abastecimiento con anchos fijos, estado con chip y acciones con tooltip.
 * @param {Object} props
 * @param {Array<Object>} props.data Órdenes ya formateadas para la tabla.
 * @param {boolean} props.isLoading Carga en curso.
 * @param {boolean} [props.actionsDisabled] Deshabilita las acciones mientras se procesa algo.
 * @param {Function} props.onView Abre el detalle de la orden.
 * @param {Function} [props.onEdit] Abre la edición de un borrador (solo con permiso).
 * @param {Function} [props.LoadingComponent] Componente de carga del cuerpo de la tabla.
 * @returns {JSX.Element}
 */
const SupplyOrdersDataTable = ({
  data,
  sort,
  onSort,
  isLoading,
  actionsDisabled = false,
  onView,
  onEdit,
  LoadingComponent = TableBodySkeleton,
}) => (
  <GeneralTableBox fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <SortableHeadTableCell
              key={column.label}
              sx={column.sx}
              sortKey={column.sortKey}
              sort={sort}
              onSort={onSort}
            >
              {column.label}
            </SortableHeadTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <LoadingComponent />
        ) : data && data.length > 0 ? (
          data.map((order, index) => (
            <TableRow key={order.uuid || index} hover>
              <RowTableCell sx={{ ...textSx, ...NUMBER_CELL_SX }} title={order.number}>
                {order.number}
              </RowTableCell>
              <RowTableCell sx={textSx} title={order.origin_label}>
                {order.origin_label}
              </RowTableCell>
              <RowTableCell sx={textSx} title={order.destination_label}>
                {order.destination_label}
              </RowTableCell>
              <RowTableCell sx={textSx}>
                <SupplyOrderProjectChips labels={order.project_labels} />
              </RowTableCell>
              <RowTableCell sx={textSx}>
                <MaterialArticleChips
                  articles={order.articles}
                  maxLabelLength={SUPPLY_ORDER_ARTICLE_CHIP_CONFIG.maxLabelLength}
                  chipSx={{ maxWidth: '100%', fontSize: SUPPLY_ORDER_ARTICLE_CHIP_CONFIG.fontSize }}
                />
              </RowTableCell>
              <RowTableCell sx={textSx}>
                <DateTimeText value={order.order_date} />
              </RowTableCell>
              <RowTableCell sx={textSx}>
                <DateTimeText
                  value={order.source_type === SOURCE_TYPES.PROVIDER
                    ? order.provider_delivery_commitment_at
                    : null}
                />
              </RowTableCell>
              <RowTableCell>
                <SupplyOrderStatusChip status={order.status} />
              </RowTableCell>
              <RowTableCell sx={textSx}>
                {order.external_order_numbers.length
                  ? <SupplyOrderProjectChips labels={order.external_order_numbers} />
                  : '-'}
              </RowTableCell>
              <RowTableCell>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <Tooltip title={TEXTS.view}>
                    <span>
                      <ActionIconButton
                        variant="view"
                        onClick={() => onView(order)}
                        disabled={actionsDisabled}
                      />
                    </span>
                  </Tooltip>
                  {order.status === SUPPLY_ORDER_STATUSES.DRAFT && onEdit && (
                    <Tooltip title={TEXTS.edit}>
                      <span>
                        <ActionIconButton
                          variant="edit"
                          onClick={() => onEdit(order)}
                          disabled={actionsDisabled}
                        />
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </RowTableCell>
            </TableRow>
          ))
        ) : (
          <NoDataSkeleton colSpan={COLUMNS.length} message={TEXTS.empty} />
        )}
      </TableBody>
    </Table>
  </GeneralTableBox>
);

export default SupplyOrdersDataTable;
